import useLabelledAreas from 'hooks/useLabelledAreas';
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import L, { LeafletEvent, ZoomAnimEvent } from 'leaflet';
import { ImageOverlay, Map as LeafletMap, Pane } from 'react-leaflet';
import { areaToTooltipData, getMinimumZoomThatFits, isDistrict, xy } from 'components/Map/util';
import {
  MAP_BASE_URL,
  MAP_HEIGHT,
  MAP_MAX_ZOOM,
  MAP_WIDTH,
  MIN_ZOOM_LEVEL_FOR_DESTINATIONS
} from 'components/Map/constants';
import ReactLeafletPixiOverlay from 'components/Map/ReactLeafletPixiOverlay';
import Hitbox from './Hitbox';
import DistrictLabelLayer from 'components/Map/DistrictLabelLayer';
import getStateAwareAreas from 'selectors/map/getStateAwareAreas';
import { ModalTooltip } from 'components/ModalTooltip/ModalTooltipContainer';
import { IAppState } from 'types/app';
import { IArea, IStateAwareArea, IStateAwareAreaWithHitbox } from 'types/map';
import PlayerMarkers from '../PlayerMarkers';
import CRS from './CRS';
import ZoomControl from 'components/Map/PixiMap/ZoomControl';

const SAFE_AREA_PADDING = 10;
const HITBOX_CLICK_ZOOM_LEVEL = 4;

interface Props {
  areas: IStateAwareArea[],
  currentArea: IArea,
  initialCenter: number[],
  initialZoom: number,
  onAreaClick: (e: any, area: IArea) => Promise<void>,
  onAreaSelect: (area?: IArea) => void,
  onClick: (_: { latlng: any }) => any,
  onHitboxClick: (area: IArea) => void,
  onMoveEnd: (e: LeafletEvent) => void,
  onZoomEnd: (e: ZoomAnimEvent) => void,
  selectedArea?: IStateAwareArea,
  zoomLevel: number,
}

export function PixiMap(props: Props) {
  const {
    areas,
    currentArea,
    initialCenter,
    initialZoom,
    onAreaClick,
    onAreaSelect,
    onClick,
    onMoveEnd,
    onZoomEnd,
    selectedArea,
  } = props;

  const mapRef = useRef<any>(null);
  const overlayRef = useRef<any>(null);

  const [isModalTooltipOpen, setIsModalTooltipOpen] = useState(false);
  const [tooltipData, setTooltipData] = useState({});
  const [zoomLevel, setZoomLevel] = useState(initialZoom);

  useEffect(() => {
    if (overlayRef.current !== null) {
      overlayRef.current!.redraw({ type: 'selectedArea', payload: { selectedArea } });
    }
  }, [selectedArea]);

  const zoomToDistrict = useCallback((area: IArea) => {
    if (mapRef.current === null) {
      return;
    }
    const destination = new L.LatLng(area.labelY!, area.labelX!);
    mapRef.current.leafletElement.setView(destination, HITBOX_CLICK_ZOOM_LEVEL, { animate: true });
  }, []);

  const handleAreaClick = useCallback(async (e: SyntheticEvent<Element, Event>, area: IArea) => {
    if (isDistrict(area) && zoomLevel < MIN_ZOOM_LEVEL_FOR_DESTINATIONS) {
      zoomToDistrict(area);
      return;
    }
    await onAreaClick(e, area);
  }, [onAreaClick, zoomLevel, zoomToDistrict]);

  const handleHitboxClick = (area: IStateAwareArea) => {
    if (area.isDistrict && !area.isLit) {
      return;
    }
    if (mapRef.current === null) {
      return;
    }
    // We need to handle the hitbox click slightly differently, depending on zoom level.
    // If we are zoomed way out, then we zoom in. Otherwise, we move to the area.
    if (zoomLevel < MIN_ZOOM_LEVEL_FOR_DESTINATIONS) {
      return zoomToDistrict(area);
    }
    return onAreaClick(null, area);
  };

  const handleHitboxTap = useCallback((area) => {
    if (area.isDistrict && !area.isLit) {
      return;
    }

    if (area.isDistrict && zoomLevel < MIN_ZOOM_LEVEL_FOR_DESTINATIONS) {
      return zoomToDistrict(area);
    }

    onAreaSelect(area);

    const baseTooltipData = areaToTooltipData(area, currentArea, (e) => onAreaClick(e, area));
    setIsModalTooltipOpen(true);

    if (zoomLevel >= MIN_ZOOM_LEVEL_FOR_DESTINATIONS) {
      return setTooltipData(baseTooltipData);
    }
  }, [currentArea, onAreaClick, onAreaSelect, zoomLevel, zoomToDistrict]);

  const handleRequestCloseModalTooltip = useCallback(() => {
    setIsModalTooltipOpen(false);
    onAreaSelect();
  }, [onAreaSelect]);

  const handleZoomEnd = useCallback((e) => {
    const zoomLevel = e.target.getZoom();
    setZoomLevel(zoomLevel);
    onZoomEnd(e);

    // Force-deselect if we've zoomed out beyond the threshold for showing destinations
    if (selectedArea?.isDistrict && zoomLevel < MIN_ZOOM_LEVEL_FOR_DESTINATIONS) {
      onAreaSelect();
    }
  }, [onAreaSelect, onZoomEnd, selectedArea]);

  // Memoize
  const labelledDistrictAreas = useLabelledAreas(areas.filter(a => a.isDistrict && a.shouldAppearOnMap));
  const labelledNonDistrictAreas = useLabelledAreas(areas.filter(a => !isDistrict(a) && a.shouldAppearOnMap));
  const minZoom = useMemo(() => Math.max(3, getMinimumZoomThatFits(window)), []);

  const areasWithHitboxes = useMemo(() => {
    return areas
      .filter(a => a.hasHitbox && a.shouldHaveHitboxAtZoomLevel(zoomLevel))
      .map(a => a as IStateAwareAreaWithHitbox);
  }, [areas, zoomLevel]);

  // noinspection PointlessArithmeticExpressionJS
  return (
    <LeafletMap
      ref={mapRef}
      attributionControl={false}
      maxBounds={[
        [0 - SAFE_AREA_PADDING, 0 + SAFE_AREA_PADDING],
        [-3000 + SAFE_AREA_PADDING, 5220 - SAFE_AREA_PADDING],
      ]}
      bounceAtZoomLimits={false}
      maxBoundsViscosity={1}
      center={xy(initialCenter[0], initialCenter[1])}
      crs={CRS}
      zoom={zoomLevel}
      minZoom={minZoom}
      maxZoom={MAP_MAX_ZOOM}
      zoomDelta={0.5}
      zoomSnap={0.5}
      onClick={onClick}
      onmoveend={onMoveEnd}
      onzoomend={handleZoomEnd}
      tms={false}
      zoomControl={false}
    >

      <Pane
        style={{ zIndex: 99 }} // Hide this pane if we can show basically anything else
      >
        {(
          <ImageOverlay
            url={`${MAP_BASE_URL}/fallback/fallback-dark.jpg`}
            bounds={L.latLngBounds(
              xy(0, 0),
              xy(MAP_WIDTH, -MAP_HEIGHT),
            )}
          />
        )}
      </Pane>

      <ZoomControl
        setZoomLevel={setZoomLevel}
        zoomLevel={zoomLevel}
        zoomDelta={0.3}
      />

      <ReactLeafletPixiOverlay
        // @ts-ignore
        selectedArea={selectedArea}
      />

      {areasWithHitboxes
        .map(area => (
          <Hitbox
            key={area.areaKey}
            area={area}
            onAreaSelect={onAreaSelect}
            onClick={handleHitboxClick}
            zoomLevel={zoomLevel}
          />
        ))}

      <DistrictLabelLayer
        areas={labelledDistrictAreas}
        areaType="District"
        currentArea={currentArea}
        onAreaClick={handleAreaClick}
        onAreaSelect={onAreaSelect}
        onTapAtLowZoomLevel={handleHitboxTap}
        zoomLevel={zoomLevel}
      />

      <DistrictLabelLayer
        areas={labelledNonDistrictAreas}
        areaType="Destination"
        currentArea={currentArea}
        minimumZoomLevel={MIN_ZOOM_LEVEL_FOR_DESTINATIONS}
        onAreaClick={onAreaClick}
        onAreaSelect={onAreaSelect}
        onTapAtLowZoomLevel={handleHitboxTap}
        zoomLevel={zoomLevel}
      />

      {/* <PlayerMarkerLayer /> */}
      <PlayerMarkers />

      <ModalTooltip
        modalIsOpen={isModalTooltipOpen}
        onRequestClose={handleRequestCloseModalTooltip}
        tooltipData={tooltipData}
        disableTouchEvents
      />
    </LeafletMap>
  );
}

const mapStateToProps = (state: IAppState) => ({ areas: getStateAwareAreas(state) });

export default connect(mapStateToProps)(PixiMap);